import { useEffect, useRef, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import { faEye } from '@fortawesome/free-solid-svg-icons'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import { Box } from '@mui/material'
import RowStatus from '../../../components/RowStatus'
import {
  EquipmentEnum,
  EquipmentEnumPtBRLabelById,
} from '../../../enums/equipment-enum'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { scannerEquipmentFields } from '../../../constants/forms/equipmentFields'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import useForm from '../../../hooks/useForm'
import Form from '../../../components/forms/Form'
import FormScanner from './components/FormScanner'
import { equipmentsStatusColorMap } from '../../../utils/statusColorMap'
import TableDetails from '../../../components/table/TableDetails'
import useLocalStorage from '../../../hooks/useLocalStorage'
import { useDebounceKeyboard } from '../../../hooks/useDebounceKeyboard'
import dayjs from 'dayjs'

const ScannerEquipmentPage = () => {
  const { canAccess } = usePermissions()

  const navigate = useNavigate()
  const location = useLocation()

  const [open, setOpen] = useState(false)
  const [hasMixedStatuses, setHasMixedStatuses] = useState(false)

  const [scannerEquipmentSection] = scannerEquipmentFields

  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues([scannerEquipmentSection]),
    [scannerEquipmentSection]
  )

  const { post: baseServicePost, formErrors } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const equipmentNumber = formData?.equipment?.number
  const [equipmentsState, setEquipmentsState] = useState([])

  const scannableStatusIds = [
    EquipmentEnum.RETURNED,
    EquipmentEnum.REMOVED_FROM_SHIPMENT,
    ]

  const scannableEquipments = equipmentsState.filter((item) =>
    scannableStatusIds.includes(item.status_id)
  )

  const debouncedEquipmentNumber = useDebounceKeyboard(equipmentNumber, 400)

  const {
    data: equipmentsData,
    isLoading: isLoadingEquipments,
    refetch: searchEquipments,
  } = useQueryWithLoader({
    queryKey: ['equipments', debouncedEquipmentNumber],
    queryFn: async () => {
      const response = await baseServicePost(
        entities.equipments.findByNumber,
        {
          numbers: [debouncedEquipmentNumber],
          apply_validation: false,
        },
        { skipNotification: true }
      )
      return response.result
    },
    select: (data) => {
      return {
        equipments: data?.map((equipment) => {
          const lastMovement = equipment.last_relevant_movement_item

          // ✅ DATA: movement_date ou updated_at do último movimento
          const movementDate =
            lastMovement?.movement?.movement_date ??
            lastMovement?.movement?.updated_at ??
            lastMovement?.movement_date ??
            equipment.last_movement?.movement_date

          // ✅ CLIENTE: recipient_customer do último movimento (quem está devolvendo)
          const customer =
            lastMovement?.movement?.recipient_customer?.name ??
            lastMovement?.movement?.recipient_customer?.alias ??
            '-'

          return {
            id: equipment.id,
            name: equipment.name,
            number: equipment.number,
            serial_number: equipment.serial_number,
            status: EquipmentEnumPtBRLabelById[equipment?.status_id] || '-',
            status_id: equipment.status_id,
            movement_id:
              lastMovement?.movement_id ?? lastMovement?.movement?.id ?? null,
            movement_item_id: lastMovement?.id ?? null,
            customer,
            movement_date_formatted: movementDate
              ? dayjs(movementDate).format('DD/MM/YYYY HH:mm')
              : '-',
            isDeleted: !!equipment.deleted_at,
          }
        }),
        totalPages: data.last_page,
      }
    },
    enabled: !!debouncedEquipmentNumber && debouncedEquipmentNumber.length > 3,
    retry: 1,
  })

  const { equipments = [] } = equipmentsData || {}

  const { load, save, clear } = useLocalStorage('returnConfirmations')

  useEffect(() => {
    const equipments = load()
    setEquipmentsState(equipments)
  }, [load])

  const lastProcessedEquipmentId = useRef(null)

  useEffect(() => {
    if (!equipments || !equipments.length) return

    const equipment = equipments[0]

    if (!equipment?.id) return

    if (lastProcessedEquipmentId.current === equipment.id) {
      return
    }

    if (equipmentsState.some((e) => e?.id === equipment?.id)) {
      showNotification('warning', 'Equipamento já adicionado na lista')
      lastProcessedEquipmentId.current = equipment.id
      return
    }

    setEquipmentsState((prev) =>
      prev.some((e) => e?.id === equipment?.id) ? prev : [...prev, equipment]
    )

    lastProcessedEquipmentId.current = equipment.id

    resetForm()
  }, [equipments, equipmentsState, resetForm, showNotification])

  useEffect(() => {
    if (equipmentsState.length === 0) return
    save(equipmentsState, true)
  }, [save, equipmentsState])

  const handleSearch = () => {
    searchEquipments()
  }

  const handleClear = () => {
    clear('returnConfirmations')
    setEquipmentsState([])
    resetForm()
  }

  const handleConfirmAll = () => {
    if (equipmentsState.length === 0) {
      showNotification('warning', 'Nenhum equipamento na lista')
      return
    }

    if (scannableEquipments.length === 0) {
      showNotification(
        'warning',
        'Nenhum equipamento em devolução ou retirado do carregamento na lista'
      )
      return
    }

    const hasReturned = scannableEquipments.some(
      (item) => item.status_id === EquipmentEnum.RETURNED
    )
    const hasRemovedFromShipment = scannableEquipments.some(
      (item) => item.status_id === EquipmentEnum.REMOVED_FROM_SHIPMENT
    )

    setHasMixedStatuses(hasReturned && hasRemovedFromShipment)
    setOpen(true)
  }

  const columns = [
    { key: 'number', label: 'Número' },
    { key: 'name', label: 'Descrição', width: '25%' },
    { key: 'serial_number', label: 'Número de Série' },
    { key: 'status', label: 'Status' },
    { key: 'customer', label: 'Cliente' }, // ✅ TROCA: Organização → Cliente
    {
      key: 'movement_date_formatted',
      label: 'Data do Movimento',
      align: 'center',
    },
  ]

  useEffect(() => {
    if (isLoadingEquipments) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isLoadingEquipments, showLoader, hideLoader])

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const actions = [
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver equipamentos',
      onClick: (equipment) =>
        navigate(`/aluguel/equipamentos/detalhes/${equipment.id}`),
      show: (equipment) =>
        !equipment.isDeleted && canAccess('Ver equipamentos'),
    },
  ]

  const handleSubmitForm = async () => {
    return
  }

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Equipamentos escaneados"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmitForm}
          textSubmit="Localizar"
          textLoadingSubmit="Buscando..."
          handleBack={() => {}}
          showButtons={false}
          isLoading={false}>
          {() => (
            <FormScanner
              fields={scannerEquipmentSection?.fields}
              formData={formData}
              handleChange={handleChange}
              formErrors={formErrors}
              actions={{
                search: handleSearch,
                confirmAll: handleConfirmAll,
                clearList: handleClear,
              }}
              data={scannableEquipments}
              modal={{
                open,
                onClose: () => {
                  setOpen(false)
                  setHasMixedStatuses(false)
                },
                onClear: handleClear,
                hasMixedStatuses,
              }}
            />
          )}
        </Form>

        <Box
          sx={{
            mt: 4,
          }}>
          <RowStatus />
        </Box>

        <Box sx={{ my: 2 }}>
          <TableDetails
            data={equipmentsState}
            columns={columns}
            actions={actions}
            rowColorConfig={{
              colorMap: equipmentsStatusColorMap,
              propertyKey: 'status_id',
            }}
          />
        </Box>
      </div>
    </MainLayout>
  )
}

export default ScannerEquipmentPage
